import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Landmark, Waves, AlertTriangle, Users, Clock, TrendingUp, MapPin } from 'lucide-react';

// DashboardPreview: A polished mock of the senior-official command-view
// dashboard. Every metric is grounded in a real operational meaning.
export function DashboardPreview() {
  return (
    <Card className="p-5 lg:p-6 border-border overflow-hidden" data-testid="dashboard-preview">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Portfolio command centre</div>
          <div className="font-display font-semibold text-[19px] mt-1">Hydropower portfolio · India</div>
        </div>
        <Badge variant="outline" className="text-[10px] uppercase tracking-widest">Demonstration environment</Badge>
      </div>

      {/* KPI row */}
      <div className="mt-5 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Kpi Icon={Landmark} label="Assets monitored" value="3" hint="Tehri · Bhakra · Sardar Sarovar" />
        <Kpi Icon={MapPin} label="Area monitored" value="6,318 km²" hint="Aggregated boundary" />
        <Kpi Icon={Clock} label="Latest observation" value="21 sec ago" hint="Ingested" />
        <Kpi Icon={AlertTriangle} label="Validated alerts" value="4" hint="Requires review" tone="warn" />
        <Kpi Icon={AlertTriangle} label="Critical incidents" value="1" hint="Assigned" tone="danger" />
        <Kpi Icon={Users} label="Field verifications pending" value="2" hint="Assigned" />
        <Kpi Icon={TrendingUp} label="Portfolio risk" value="64.3" hint="Out of 100" />
      </div>

      {/* Chart + map row */}
      <div className="mt-5 grid md:grid-cols-2 gap-4">
        <div className="rounded-lg border border-border p-4">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Alerts by category (last 30 days)</div>
          <div className="mt-4 space-y-2.5">
            {[
              ['Terrain disturbance', 34, 'hsl(38 92% 50%)'],
              ['Encroachment', 26, 'hsl(200 80% 45%)'],
              ['Water change', 22, 'hsl(173 72% 40%)'],
              ['Fire / Flood', 18, 'hsl(0 74% 55%)'],
            ].map(([label, pct, color]) => (
              <div key={label}>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">{label}</span>
                  <span className="font-mono tabular-nums">{pct}%</span>
                </div>
                <div className="mt-1 h-1.5 bg-secondary rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-lg border border-border p-4">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Asset risk map (portfolio)</div>
          <div className="mt-4 space-y-3">
            {[
              ['Tehri Dam Complex', 'Uttarakhand', 78, 'High'],
              ['Bhakra Dam Complex', 'Himachal Pradesh', 52, 'Moderate'],
              ['Sardar Sarovar Dam Complex', 'Gujarat', 46, 'Moderate'],
            ].map(([name, region, score, level]) => (
              <div key={name} className="flex items-center gap-3">
                <div className="h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-mono font-semibold" style={{ background: score > 70 ? 'hsl(0 74% 55% / 0.14)' : score > 50 ? 'hsl(38 92% 50% / 0.14)' : 'hsl(173 72% 40% / 0.14)', color: score > 70 ? 'hsl(0 74% 45%)' : score > 50 ? 'hsl(38 92% 40%)' : 'hsl(173 72% 32%)' }}>
                  {score}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{name}</div>
                  <div className="text-[11px] text-muted-foreground">{region}</div>
                </div>
                <Badge variant="outline" className={`text-[10px] ${score > 70 ? 'border-red-500/40 text-red-600' : score > 50 ? 'border-amber-500/40 text-amber-600' : 'border-primary/40 text-primary'}`}>
                  {level}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}

function Kpi({ Icon, label, value, hint, tone }) {
  const toneClass = tone === 'warn' ? 'text-amber-600 dark:text-amber-400' : tone === 'danger' ? 'text-red-600 dark:text-red-400' : '';
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="flex items-center gap-1.5">
        <Icon size={12} className="text-muted-foreground" />
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold truncate">{label}</div>
      </div>
      <div className={`mt-2 font-display font-semibold text-[20px] ${toneClass}`}>{value}</div>
      <div className="mt-0.5 text-[10px] text-muted-foreground truncate">{hint}</div>
    </div>
  );
}
